/*
 * GrowthBook reader — Size Guide A/B test.
 *
 * Experiment: 'size_guide_v1'
 * Feature flag: 'size_guide_v1' (string; values: control|v2)
 *
 * Writes html[data-gb-size-guide="v2"] when the user is in variant B. The
 * attribute is the single source of truth read by src/js/popups.js at click
 * time — it swaps the button's data-id from "<variantId>" to "<variantId>-v2"
 * so the new drawer opens instead of the old one. The v2 drawer markup is
 * rendered in parallel by snippets/size-guide.liquid.
 *
 * Depends on assets/growthbook-abtest.js (core):
 *   - checkHoldout() and window.ab_enabled managed by the core
 *   - the core does the single queue push and calls window.applySizeGuide
 *     from its applyFeatureFlags (initial + on each growthbookdata)
 *
 * No pre-first-paint bootstrap: the size guide is hidden by default and
 * only becomes visible on click, so there is no flash risk. The reader
 * is authoritative and runs before the user can realistically click.
 *
 * URL override:
 *   ?size_guide=v2        → force v2, persists in sessionStorage
 *   ?size_guide=control   → force control, persists
 *   ?size_guide=off       → clear override, back to normal flow
 */
(() => {
  const EXPERIMENT_KEY = 'size_guide_v1';
  const OVERRIDE_PARAM = 'size_guide';
  const OVERRIDE_STORAGE_KEY = 'gb_size_guide_override';

  const hasSizeGuideButton = () => !!document.querySelector('.sizechart-js-btn');

  const readOverride = () => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);
      if (fromUrl === 'off') {
        sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
        return null;
      }
      if (fromUrl !== null) {
        sessionStorage.setItem(OVERRIDE_STORAGE_KEY, fromUrl);
        return fromUrl;
      }
      return sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  let lastPersistedVariant = null;
  const persistVariantForTracking = (value) => {
    if (value === lastPersistedVariant) return;
    lastPersistedVariant = value;
    try {
      document.cookie = `gb_${EXPERIMENT_KEY}_variant=${value}; path=/; max-age=2592000; SameSite=Lax`;
    } catch (e) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'gb_variant_ready',
      experiment_id: EXPERIMENT_KEY,
      variation_id: value,
    });
  };

  const applyVariantToRoot = (variant) => {
    if (variant === 'v2') {
      document.documentElement.dataset.gbSizeGuide = 'v2';
    } else {
      delete document.documentElement.dataset.gbSizeGuide;
    }
  };

  // Mirrors the readiness check in the carousel reader: the core's push can
  // fire before the features payload lands, in which case every
  // getFeatureValue call returns the default. Wait for the payload.
  const isGrowthBookReady = (gb) => {
    if (!gb) return false;
    if (gb.ready === true) return true;
    try {
      const features = typeof gb.getFeatures === 'function' && gb.getFeatures();
      return !!features && Object.keys(features).length > 0;
    } catch (e) {
      return false;
    }
  };

  window.applySizeGuide = (gb) => {
    if (!hasSizeGuideButton()) return;

    const override = readOverride();
    if (override !== null) {
      window.ab_enabled = true;
      applyVariantToRoot(override);
      persistVariantForTracking(override === 'v2' ? 'v2' : 'control');
      return;
    }

    if (!isGrowthBookReady(gb)) return;

    if (!window.ab_enabled) {
      applyVariantToRoot('control');
      persistVariantForTracking('holdout');
      return;
    }

    const variant = gb.getFeatureValue(EXPERIMENT_KEY, 'control');
    applyVariantToRoot(variant);
    persistVariantForTracking(variant);
  };

  // RACE: if GrowthBook was already initialized when the core pushed its
  // callback, that push ran BEFORE this file defined window.applySizeGuide
  // — and if no other 'growthbookdata' fires, the flag never lands.
  // Auto-apply here if the SDK is already available; idempotent with the
  // core's call.
  if (window._growthbook) window.applySizeGuide(window._growthbook);
})();
